/**
 * BadgeIconService - Maps badge types and categories to Lucide icons
 * Cross-platform compatible (lucide-react on web, lucide-react-native on mobile)
 */

import {
  // Authority / Leadership
  Crown,
  Shield,
  ShieldCheck,
  Sword,
  Castle,
  Flag,
  ClipboardCheck,
  Briefcase,
  HardHat,
  KeyRound,
  Lock,

  // Location
  Plane,
  Building2,
  MapPin,

  // Equipment / Vehicles
  Car,
  Zap,
  Bike,
  Bus,
  ParkingSquare,

  // Training / Mentor
  GraduationCap,
  Users,
  UserCheck,
  UserPlus,

  // Performance / Achievement
  Star,
  Stars,
  TrendingUp,
  Target,
  Trophy,
  Award,
  Medal,
  Flame,
  Gem,
  Diamond,

  // Seasonal
  Sun,
  Snowflake,
  PartyPopper,
  TreePine,

  // Emergency
  Ambulance,
  AlertTriangle,
  Heart,
  ShieldAlert,

  // Business behavior
  Clock,
  Sunrise,
  Mountain,
  Calendar,
  CalendarCheck,
  Search,
  Phone,
  Network,
  DollarSign,
  Wallet,

  // Special
  Rocket,
  Compass,
  Moon,
  MoonStar,
  Sparkles,
  Receipt,
  BadgeCheck,
  Wrench,
  CircleUser,

  // Department Roles
  Calculator,
  FileText,
  CreditCard,
  ShoppingCart,
  Smartphone,
  Globe,
  Palette,
  Shirt,
  Share2,
  ClipboardList,
  Banknote,
  FileWarning,
  BookOpen,
  CalendarDays,
  FileSearch,
  Mail,
  Scale,
  FileSignature,
  PhoneCall,
  CirclePlus,
  CircleMinus,
  CarFront,

  // Defaults
  Circle,
  CircleDot,
} from 'lucide-react';
import React from 'react';

// Custom Chess Knight icon for KOT (Kings of Tomorrow) - Vegas Gold
// Accepts same props as Lucide icons for compatibility
// eslint-disable-next-line no-unused-vars
const ChessKnight = ({ className, style, size = 24, strokeWidth, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width={size}
    height={size}
    className={className}
    style={style}
    {...props}
  >
    <path d="M19 22H5v-1h14v1zm-2-2H7v-1c0-.55.45-1 1-1h8c.55 0 1 .45 1 1v1zm-1-3H8c0-1 .5-2 1-3.5.5-1.5.5-2.5.5-3 0-2.5 1.5-5 3.5-6.5.3-.2.7-.1.9.2l3 4c.2.3.1.7-.2.9l-1.5 1c-.3.2-.4.5-.3.8l.5 1.5c.1.4-.1.8-.5.9l-.4.2c-.2.1-.4.3-.5.5H15zm-6-9c0 .8.3 1.5.9 2 .1.1.1.2.1.3 0 .2-.2.4-.4.4-.4 0-.8-.2-1.1-.5-.5-.5-.8-1.3-.8-2.2 0-1.4 1.1-2.5 2.5-2.5.1 0 .2 0 .3.1.2.1.3.3.2.5-.1.3-.4.5-.7.5-.6 0-1 .6-1 1.4z"/>
  </svg>
);

export class BadgeIconService {
  /**
   * Get icon component for a badge based on type and category
   */
  static getBadgeIcon(badgeType, category) {
    const specificMappings = {
      // Access badges - Hierarchy
      'founder': Crown,
      'exec': KeyRound,
      'council': Castle,
      'kot': ChessKnight,
      'captain': HardHat,
      'bread-breaker': Banknote,
      'payroll-manager': Wallet,
      'client': CircleUser,

      // Legacy access (in case still referenced)
      'platform-owner': Crown,
      'architect': Crown,
      'architect-supreme': Crown,
      'super-admin': ShieldCheck,
      'admin-access': KeyRound,
      'council-elder': Castle,
      'kot-manager': ChessKnight,
      'manager': ChessKnight,
      'supervisor': ClipboardCheck,

      // Location badges
      'vegas-strip-certified': Sparkles,
      'airport-specialist': Plane,
      'downtown-navigator': Building2,
      'location-manager': MapPin,

      // Equipment badges
      'luxury-vehicle-master': Car,
      'ev-specialist': Zap,
      'motorcycle-certified': Bike,

      // Training badges
      'master-trainer': GraduationCap,
      'senior-mentor': UserCheck,
      'peer-mentor': Users,

      // Performance badges
      'perfect-month': Medal,
      'top-performer': TrendingUp,
      'five-star-machine': Stars,
      'milestone-master': Target,

      // Progression badges
      'goat': Trophy,
      'legend': Flame,
      'immortal': Gem,
      'unicorn': Diamond,

      // Seasonal badges
      'holiday-hero-2025': TreePine,
      'summer-warrior-2025': Sun,
      'winter-warrior': Snowflake,
      'new-year-champion': PartyPopper,

      // Emergency badges
      'emergency-responder': Ambulance,
      'emergency-hero': AlertTriangle,
      'life-saver': Heart,
      'guardian-angel': ShieldAlert,

      // Business behavior badges
      'always-on-time': Clock,
      'early-bird': Sunrise,
      'reliability-rock': Mountain,
      'weekend-champion': Calendar,
      'weekend-warrior': Calendar,
      'talent-scout': Search,
      'client-hunter': Target,
      'phone-pro': Phone,
      'network-builder': Network,
      'revenue-generator': DollarSign,

      // Special achievements
      'founding-member': Rocket,
      'platform-pioneer': Compass,
      'loyalty-legend': Award,
      'odv-ambassador': Trophy,

      // Legacy role badges (deactivated but keep mappings)
      'vip-concierge': Gem,
      'event-coordinator-supreme': CalendarCheck,
      'night-shift-commander': Moon,

      // Legacy roster positions (deactivated but keep mappings)
      'valet': ParkingSquare,
      'valet-certified': BadgeCheck,
      'front_desk': Receipt,
      'shuttle': Bus,
      'overnight': MoonStar,

      // ========================================
      // DEPARTMENT ROLE BADGES (Breaking Bread)
      // ========================================

      // Finance Department
      'finance-bookkeeping': Calculator,
      'finance-invoicing': FileText,
      'finance-collections': CreditCard,
      'finance-purchasing': ShoppingCart,
      'finance-budget': Scale,

      // Branding Department
      'branding-app-dev': Smartphone,
      'branding-website': Globe,
      'branding-graphics': Palette,
      'branding-apparel': Shirt,
      'branding-social-media': Share2,

      // HR Department
      'hr-recruiting': UserPlus,
      'hr-onboarding': ClipboardList,
      'hr-payroll': Banknote,
      'hr-writeups': FileWarning,
      'hr-development': BookOpen,
      'hr-bonus-fines': CirclePlus,

      // Operations Department
      'ops-shuttle': CarFront,
      'ops-events': CalendarDays,
      'ops-claims': FileSearch,
      'ops-equipment': Wrench,

      // Admin Department
      'admin-emails': Mail,
      'admin-legal': Scale,
      'admin-contracts': FileSignature,
      'admin-phones': PhoneCall,
    };

    if (specificMappings[badgeType]) {
      return specificMappings[badgeType];
    }

    // Category fallbacks
    const categoryMappings = {
      access: Shield,
      authority: ShieldCheck,
      authorization: Shield,
      achievement: Trophy,
      training: GraduationCap,
      mentor: GraduationCap,
      location: MapPin,
      equipment: Wrench,
      seasonal: Calendar,
      roster: Users,
      role: Briefcase,
      certification: BadgeCheck,
      progression: TrendingUp,
      sales: DollarSign,
      recruitment: Network,
      performance: Target,
      gamification: Sparkles,
    };

    if (categoryMappings[category]) {
      return categoryMappings[category];
    }

    return Award;
  }

  /**
   * Get rarity styling (for gamification badges only)
   */
  static getRarityStyle(rarity) {
    const styles = {
      common: {
        bgColor: 'bg-zinc-800/50',
        borderColor: 'border-zinc-600',
        textColor: 'text-zinc-400',
      },
      uncommon: {
        bgColor: 'bg-emerald-900/40',
        borderColor: 'border-emerald-500/50',
        textColor: 'text-emerald-400',
      },
      rare: {
        bgColor: 'bg-blue-900/40',
        borderColor: 'border-blue-500/50',
        textColor: 'text-blue-400',
      },
      epic: {
        bgColor: 'bg-purple-900/40',
        borderColor: 'border-purple-500/50',
        textColor: 'text-purple-400',
      },
      legendary: {
        bgColor: 'bg-amber-900/40',
        borderColor: 'border-amber-500/50',
        textColor: 'text-amber-400',
      },
      mythical: {
        bgColor: 'bg-pink-900/40',
        borderColor: 'border-pink-500/50',
        textColor: 'text-pink-400',
      },
    };

    return styles[rarity] || styles.common;
  }

  /**
   * Get category display info
   */
  static getCategoryInfo(category) {
    const info = {
      access: { name: 'Access', color: '#EF4444' },
      authority: { name: 'Authority', color: '#EF4444' },
      achievement: { name: 'Achievement', color: '#AEA36D' },
      training: { name: 'Training', color: '#3B82F6' },
      location: { name: 'Location', color: '#10B981' },
      equipment: { name: 'Equipment', color: '#6366F1' },
      seasonal: { name: 'Seasonal', color: '#EC4899' },
      role: { name: 'Role', color: '#F59E0B' },
      performance: { name: 'Performance', color: '#22C55E' },
      gamification: { name: 'Gamification', color: '#A855F7' },
    };

    return info[category] || { name: category || 'General', color: '#6B7280' };
  }
}

export const getBadgeIcon = (badgeType, category) => BadgeIconService.getBadgeIcon(badgeType, category);
export const getRarityStyle = (rarity) => BadgeIconService.getRarityStyle(rarity);
export const getCategoryInfo = (category) => BadgeIconService.getCategoryInfo(category);
export { ChessKnight };

export default BadgeIconService;
